/**
 * Pantallas HTML superpuestas: menú, pausa, fin de partida, cámara y cuenta atrás.
 * Los botones llevan data-action; cada pulsación se entrega a onAction(acción).
 * onShow(elemento | null) avisa de cada cambio de pantalla (lo usa la navegación con el cuerpo).
 */
export class Screens {
  constructor(onAction) {
    this.screens = {
      menu: document.getElementById('screen-menu'),
      pause: document.getElementById('screen-pause'),
      over: document.getElementById('screen-over'),
      camera: document.getElementById('screen-camera'),
    };
    this.onShow = null;
    this.camShown = {}; // último estado pintado en la pantalla de la cámara (para no tocar el DOM en cada fotograma)
    this.countdown = document.getElementById('countdown');
    this.current = null;
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[data-action]');
      if (button) onAction(button.dataset.action);
    });
  }

  #field(screen, name) {
    return this.screens[screen].querySelector(`[data-field=${name}]`);
  }

  show(name) {
    this.current = name;
    for (const [key, el] of Object.entries(this.screens)) el.hidden = key !== name;
    document.body.classList.toggle('has-screen', !!name);
    this.onShow?.(name ? this.screens[name] : null);
  }

  /** Pausa, con un aviso opcional (p. ej. «No te veo»). */
  showPause(hint = '') {
    const p = this.#field('pause', 'pauseHint');
    p.textContent = hint;
    p.hidden = !hint;
    this.show('pause');
  }

  /** Botones de ajustes: control, dificultad y miniatura de la cámara. */
  setOptions({ camera, difficulty, preview }) {
    this.#field('menu', 'control').textContent = `Control: ${camera ? 'cámara' : 'táctil'}`;
    this.#field('menu', 'difficulty').textContent = `Dificultad: ${difficulty === 'soft' ? 'suave' : 'normal'}`;
    for (const el of document.querySelectorAll('[data-field=preview]')) el.textContent = `Cámara en pantalla: ${preview ? 'sí' : 'no'}`;
    for (const el of document.querySelectorAll('[data-camera-only]')) el.hidden = !camera;
  }

  /**
   * Pantalla de la cámara. s: { status ('off'|'loading'|'on'|'error'), calibrated, calibrating,
   * progress (0–1), guidance (texto), error, info }
   */
  updateCamera(s) {
    const prev = this.camShown;
    const key = JSON.stringify(s);
    if (key === prev.key) return;
    this.camShown = { key };
    const f = (name) => this.#field('camera', name);
    const on = s.status === 'on';
    f('camStart').hidden = on || s.status === 'loading';
    f('camStart').textContent = s.status === 'error' ? 'Reintentar' : 'Activar cámara';
    f('camPlay').hidden = !(on && s.calibrated);
    f('camCalibrate').hidden = !on || s.training;
    f('camSkip').hidden = !s.training;
    f('camOff').hidden = s.status === 'off';
    f('camError').hidden = !s.error;
    f('camError').textContent = s.error;
    f('camInfo').textContent = s.info;
    f('camGuidance').textContent = s.guidance;
    f('camGuidance').classList.toggle('ok', on && s.calibrated);
    f('camProgress').style.width = `${Math.round(s.progress * 100)}%`;
    f('camOverlay').textContent = s.overlay;
    f('camOverlay').hidden = !s.overlay;
  }

  hide() {
    this.show(null);
  }

  showMenu(records, missions = []) {
    this.#renderMissions(missions);
    const r = records.data;
    this.#field('menu', 'record').textContent = r.bestScore
      ? `Récord: ${r.bestScore} puntos · ${r.bestDistance} m`
      : '';
    this.#field('menu', 'help').hidden = true;
    this.#field('menu', 'wardrobe').hidden = true;
    this.show('menu');
  }

  toggleHelp() {
    const help = this.#field('menu', 'help');
    help.hidden = !help.hidden;
    if (!help.hidden) this.#field('menu', 'wardrobe').hidden = true;
  }

  #renderMissions(missions) {
    const list = this.#field('menu', 'missions');
    list.replaceChildren(
      ...missions.map((m) => {
        const li = document.createElement('li');
        li.textContent = m.text;
        const prize = document.createElement('b');
        prize.textContent = ` +${m.reward} ●`;
        li.append(prize);
        return li;
      }),
    );
  }

  toggleWardrobe() {
    const w = this.#field('menu', 'wardrobe');
    w.hidden = !w.hidden;
    if (!w.hidden) this.#field('menu', 'help').hidden = true;
  }

  /** Lista de trajes: color, nombre y precio / equipado. */
  renderWardrobe(skins, wardrobe, balance) {
    this.#field('menu', 'balance').textContent = `● ${balance} monedas`;
    const list = this.#field('menu', 'skins');
    list.replaceChildren(
      ...skins.map((s) => {
        const owned = wardrobe.owned.has(s.id);
        const b = document.createElement('button');
        b.className = `skin${s.id === wardrobe.selected ? ' selected' : ''}${!owned && balance < s.price ? ' locked' : ''}`;
        b.dataset.action = `skin:${s.id}`;
        const sw = document.createElement('span');
        sw.className = 'swatch';
        sw.style.background = `linear-gradient(135deg, #${s.jacket.toString(16).padStart(6, '0')} 55%, #${s.scarf.toString(16).padStart(6, '0')} 55%)`;
        const label = document.createElement('span');
        label.textContent = s.name;
        const sub = document.createElement('small');
        sub.textContent = s.id === wardrobe.selected ? 'Equipado' : owned ? 'Equipar' : `● ${s.price}`;
        label.append(sub);
        b.append(sw, label);
        return b;
      }),
    );
  }

  showOver({ title, hint, score, distance, coins, best, isNew, bank, missions = [], continueCost = 0 }) {
    const cont = this.#field('over', 'continue');
    cont.hidden = !continueCost;
    cont.textContent = `Seguir · ● ${continueCost}`;
    const done = missions.length ? `Misiones cumplidas: ${missions.length} (+${missions.reduce((a, m) => a + m.reward, 0)} ●) · ` : '';
    this.#field('over', 'bank').textContent = `${done}● ${bank} monedas para el vestuario`;
    this.#field('over', 'title').textContent = title;
    this.#field('over', 'hint').textContent = hint;
    this.#field('over', 'score').textContent = score;
    this.#field('over', 'distance').textContent = `${distance} m`;
    this.#field('over', 'coins').textContent = coins;
    this.#field('over', 'best').textContent = best;
    this.#field('over', 'newRecord').hidden = !isNew;
    this.show('over');
  }

  /** Texto de los botones de sonido (menú y pausa). */
  setSound(on, music) {
    for (const el of document.querySelectorAll('[data-field=sound]')) el.textContent = `Sonido: ${on ? 'sí' : 'no'}`;
    for (const el of document.querySelectorAll('[data-field=music]')) el.textContent = `Música: ${music ? 'sí' : 'no'}`;
  }

  setCountdown(n) {
    this.countdown.hidden = !n;
    this.countdown.textContent = n || '';
  }
}
